import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { AuthState, AuthContextType, LoginCredentials, RegisterCredentials, User } from '../types'
import { AuthService } from '../services/auth.service'

// Initial state
const getInitialState = (): AuthState => {
  const { accessToken, refreshToken } = AuthService.getTokens()

  return {
    user: null,
    accessToken,
    refreshToken,
    isAuthenticated: AuthService.isAuthenticated(),
    isLoading: true,
    error: null,
  }
}

const initialState = getInitialState()

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; accessToken: string; refreshToken: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }
    default:
      return state
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider component
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const { accessToken, refreshToken } = AuthService.getTokens()

      if (accessToken && refreshToken) {
        try {
          // First try to get user data with current token
          const user = await AuthService.getCurrentUser()
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user,
              accessToken,
              refreshToken
            },
          })
        } catch (error: any) {
          // If token verification failed, try to refresh the token
          try {
            const tokens = await AuthService.refreshToken()
            const user = await AuthService.getCurrentUser()

            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                user,
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken
              },
            })
          } catch (refreshError: any) {
            // Both token verification and refresh failed, clear everything
            AuthService.clearTokens()

            // Provide more specific error message
            const errorMessage = refreshError.response?.status === 401
              ? 'Session expired. Please log in again.'
              : 'Authentication failed. Please log in again.'
            dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })
          }
        }
      } else {
        // Clean up any invalid tokens
        AuthService.clearTokens()
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    checkAuth()
  }, [])

  // Login function
  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: 'AUTH_START' })
    try {
      const response = await AuthService.login(credentials)
      const { user, token: accessToken, refresh: refreshToken } = response.data

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, accessToken, refreshToken },
      })
    } catch (error: any) {
      const errorMessage = error.message || error.response?.data?.message || 'Login failed'
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })
      throw error
    }
  }

  // Register function
  const register = async (credentials: RegisterCredentials) => {
    dispatch({ type: 'AUTH_START' })
    try {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...registerData } = credentials
      const response = await AuthService.register(registerData)
      const { user, token: accessToken, refresh: refreshToken } = response.data

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, accessToken, refreshToken },
      })
    } catch (error: any) {
      const errorMessage = error.message || error.response?.data?.message || 'Registration failed'
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })
      throw error
    }
  }

  // Logout function
  const logout = () => {
    AuthService.logout()
    dispatch({ type: 'LOGOUT' })
  }

  // Refresh token function
  const refreshAccessToken = async () => {
    try {
      const tokens = await AuthService.refreshToken()

      // Update tokens in state without changing user
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: state.user!,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken
        },
      })
    } catch (error) {
      logout()
      throw error
    }
  }

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    refreshAccessToken,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

